// src/features/business-functions/hooks/useAccountDetail.js
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { accountService } from '../services/accountService';
import { adaptAccountDetail } from '../adapters/accountDetailAdapter';
import { accountDetailAtom } from '../state/accountAtoms';

function parseAccountError(err, fallbackMessage = 'Unable to load account') {
  const status = err?.response?.status || err?.status || 0;
  const raw =
    err?.response?.data?.data?.general_error ||
    err?.response?.data?.data?.system_error ||
    err?.response?.data?.message ||
    err?.message ||
    fallbackMessage;
  return {
    status,
    message: typeof raw === 'string' ? raw : fallbackMessage,
  };
}

/**
 * Fetches and manages account detail for the business function account page.
 * Separates network calls from UI rendering — components only receive the final state.
 */
export default function useAccountDetail() {
  const { accountId } = useParams();
  const [accountAtom, setAccountDetail] = useRecoilState(accountDetailAtom);
  const cachedAccount = accountAtom && accountAtom.id === accountId ? accountAtom : null;

  const [account, setLocalAccount] = useState(cachedAccount);
  const [isLoading, setIsLoading] = useState(!cachedAccount);
  const [error, setError] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!accountId) return;

    setError(null);

    try {
      const res = await accountService.getAccountDetail(accountId);
      if (res?.status === 200 || res?.status === 201) {
        const adapted = adaptAccountDetail(res.data?.data ?? res.data);
        setLocalAccount(adapted);
        setAccountDetail(adapted);
        setError(null);
        return;
      }

      setLocalAccount(null);
      setAccountDetail(null);
      setError({
        status: res?.status || 0,
        message: res?.data?.general_error || res?.data?.message || 'Unable to load account',
      });
    } catch (err) {
      setLocalAccount(null);
      setAccountDetail(null);
      setError(parseAccountError(err));
    } finally {
      setIsLoading(false);
    }
  }, [accountId, setAccountDetail]);

  useEffect(() => {
    setLocalAccount((current) => (current?.id === accountId ? current : null));
    setError(null);
    fetchDetail();
  }, [accountId, fetchDetail]);

  return {
    accountId,
    account,
    isLoading,
    error,
    isForbidden: error?.status === 403,
    refetchDetail: fetchDetail,
  };
}
